import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { usePQC } from '../hooks/usePQC';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  Upload, 
  Download, 
  Copy, 
  Eye, 
  EyeOff, 
  Shield, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Key,
  FileCheck,
  AlertTriangle,
  Info,
  Clock,
  User,
  Hash,
  Zap,
  Lock,
  Unlock
} from 'lucide-react';
import { toast } from 'sonner';

interface PQCSignatureManagerProps {
  className?: string;
}

interface SignatureResult {
  signature: string;
  data: string;
  timestamp: string;
  algorithm: string;
  publicKey: string;
  hash: string;
}

interface VerificationResult {
  isValid: boolean;
  data: string;
  timestamp: string;
  signerPublicKey?: string;
  algorithm: string;
  details: string;
}

interface DocumentSignature {
  id: string;
  fileName: string;
  signature: string;
  timestamp: string;
  hash: string;
  size: number;
}

const PQCSignatureManager: React.FC<PQCSignatureManagerProps> = ({ className }) => {
  const { user } = useAuth();
  const {
    hasValidKeys,
    signData,
    verifySignature,
    getPublicKeys,
    isSigning,
    isVerifying
  } = usePQC();

  const [activeTab, setActiveTab] = useState<'sign' | 'verify' | 'history'>('sign');
  const [documentToSign, setDocumentToSign] = useState('');
  const [signatureToVerify, setSignatureToVerify] = useState('');
  const [dataToVerify, setDataToVerify] = useState('');
  const [signerPublicKey, setSignerPublicKey] = useState('');
  const [signatureResult, setSignatureResult] = useState<SignatureResult | null>(null);
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [signatureHistory, setSignatureHistory] = useState<DocumentSignature[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const verifyFileInputRef = useRef<HTMLInputElement>(null);

  const publicKeys = getPublicKeys();

  // Função para gerar hash SHA-256
  const generateHash = async (data: string): Promise<string> => {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleSignDocument = async () => {
    if (!documentToSign.trim()) {
      toast.error('Digite o documento a ser assinado');
      return;
    }

    try {
      const signature = await signData(documentToSign);
      const hash = await generateHash(documentToSign);
      
      if (signature) {
        const result: SignatureResult = {
          signature: JSON.stringify(signature),
          data: documentToSign,
          timestamp: new Date().toISOString(),
          algorithm: 'CRYSTALS-Dilithium',
          publicKey: publicKeys.dilithium || '',
          hash
        };
        
        setSignatureResult(result);
        setShowResults(true);
        
        // Adicionar ao histórico
        const historyItem: DocumentSignature = {
          id: Date.now().toString(),
          fileName: `documento-${new Date().toISOString().split('T')[0]}`,
          signature: result.signature,
          timestamp: result.timestamp,
          hash: result.hash,
          size: documentToSign.length
        };
        
        setSignatureHistory(prev => [historyItem, ...prev]);
        setDocumentToSign('');
        toast.success('Documento assinado com sucesso');
      }
    } catch (error) {
      console.error('Erro na assinatura:', error);
      toast.error('Erro ao assinar documento');
    }
  };

  const handleVerifySignature = async () => {
    if (!signatureToVerify.trim() || !dataToVerify.trim()) {
      toast.error('Insira a assinatura e os dados para verificação');
      return;
    }

    try {
      const signature = JSON.parse(signatureToVerify);
      const isValid = await verifySignature(signature);
      
      const result: VerificationResult = {
        isValid,
        data: dataToVerify,
        timestamp: new Date().toISOString(),
        signerPublicKey: signerPublicKey || 'Não especificada',
        algorithm: 'CRYSTALS-Dilithium',
        details: isValid 
          ? 'A assinatura é válida e os dados não foram modificados'
          : 'A assinatura é inválida ou os dados foram modificados'
      };
      
      setVerificationResult(result);
      setShowResults(true);
      
      if (isValid) {
        toast.success('Assinatura verificada com sucesso');
      } else {
        toast.error('Assinatura inválida');
      }
    } catch (error) {
      console.error('Erro na verificação:', error);
      toast.error('Erro ao verificar assinatura - verifique o formato');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, target: 'sign' | 'verify') => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (target === 'sign') {
        setDocumentToSign(content);
      } else {
        setDataToVerify(content);
      }
      toast.success('Arquivo carregado com sucesso');
    };
    reader.readAsText(file);
  };

  const downloadResult = (data: string, filename: string) => {
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Arquivo baixado com sucesso');
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado para a área de transferência`);
    } catch (error) {
      toast.error('Erro ao copiar para a área de transferência');
    }
  };

  const useMyPublicKey = () => {
    if (publicKeys.dilithium) {
      setSignerPublicKey(publicKeys.dilithium);
      toast.success('Sua chave pública foi inserida');
    } else {
      toast.error('Chave pública Dilithium não encontrada');
    }
  };

  const formatDataSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const maskSensitiveData = (data: string, show: boolean) => {
    if (show) return data;
    if (data.length <= 20) return '•'.repeat(data.length);
    return data.substring(0, 10) + '•'.repeat(Math.max(0, data.length - 20)) + data.substring(data.length - 10);
  };

  const exportSignaturePackage = (signature: SignatureResult) => {
    const packageData = {
      document: signature.data,
      signature: signature.signature,
      publicKey: signature.publicKey,
      timestamp: signature.timestamp,
      algorithm: signature.algorithm,
      hash: signature.hash,
      metadata: {
        version: '1.0',
        format: 'Saúde Virtual-PQC-Signature'
      }
    };
    
    downloadResult(
      JSON.stringify(packageData, null, 2),
      `signature-package-${new Date().toISOString().split('T')[0]}.json`
    );
  };

  if (!user) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Lock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Faça login para acessar o gerenciador de assinaturas</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasValidKeys()) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Chaves PQC Necessárias</h3>
            <p className="text-gray-500 mb-4">
              Você precisa gerar suas chaves Dilithium antes de usar assinaturas digitais
            </p>
            <p className="text-sm text-gray-400">
              Acesse o Gerenciador de Chaves para criar suas chaves de assinatura
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Gerenciador de Assinaturas PQC
          </CardTitle>
          <CardDescription>
            Assine e verifique documentos usando assinaturas digitais pós-quânticas
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('sign')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'sign'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="h-4 w-4 inline mr-2" />
          Assinar
        </button>
        <button
          onClick={() => setActiveTab('verify')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'verify'
              ? 'bg-white text-green-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CheckCircle className="h-4 w-4 inline mr-2" />
          Verificar
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white text-purple-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Clock className="h-4 w-4 inline mr-2" />
          Histórico
        </button>
      </div>

      {/* Interface de Assinatura */}
      {activeTab === 'sign' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Assinar Documento
            </CardTitle>
            <CardDescription>
              Use sua chave privada Dilithium para assinar documentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Documento para Assinar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documento para Assinar
              </label>
              <div className="space-y-2">
                <textarea
                  value={documentToSign}
                  onChange={(e) => setDocumentToSign(e.target.value)}
                  placeholder="Digite ou cole o documento que deseja assinar..."
                  className="w-full p-3 border border-gray-300 rounded-md"
                  rows={8}
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => handleFileUpload(e, 'sign')}
                      accept=".txt,.json,.csv,.md"
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      size="sm"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Carregar Arquivo
                    </Button>
                  </div>
                  <span className="text-xs text-gray-500">
                    {documentToSign.length} caracteres
                  </span>
                </div>
              </div>
            </div>

            {/* Informações da Chave */}
            {publicKeys.dilithium && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Chave de Assinatura
                </h4>
                <p className="text-sm text-blue-700 font-mono break-all">
                  {publicKeys.dilithium.substring(0, 64)}...
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Algoritmo: CRYSTALS-Dilithium (Pós-Quântico)
                </p>
              </div>
            )}

            {/* Botão de Assinar */}
            <Button
              onClick={handleSignDocument}
              disabled={isSigning || !documentToSign.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isSigning ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              {isSigning ? 'Assinando...' : 'Assinar Documento'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Interface de Verificação */}
      {activeTab === 'verify' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Verificar Assinatura
            </CardTitle>
            <CardDescription>
              Verifique a autenticidade de documentos assinados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Chave Pública do Signatário */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave Pública do Signatário
              </label>
              <div className="flex gap-2">
                <textarea
                  value={signerPublicKey}
                  onChange={(e) => setSignerPublicKey(e.target.value)}
                  placeholder="Cole aqui a chave pública Dilithium do signatário..."
                  className="flex-1 p-3 border border-gray-300 rounded-md font-mono text-xs"
                  rows={3}
                />
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={useMyPublicKey}
                    variant="outline"
                    size="sm"
                    title="Usar minha própria chave pública"
                  >
                    <Key className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Documento Original */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documento Original
              </label>
              <div className="space-y-2">
                <textarea
                  value={dataToVerify}
                  onChange={(e) => setDataToVerify(e.target.value)}
                  placeholder="Cole aqui o documento original..."
                  className="w-full p-3 border border-gray-300 rounded-md"
                  rows={6}
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={verifyFileInputRef}
                      onChange={(e) => handleFileUpload(e, 'verify')}
                      accept=".txt,.json,.csv,.md"
                      className="hidden"
                    />
                    <Button
                      onClick={() => verifyFileInputRef.current?.click()}
                      variant="outline"
                      size="sm"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Carregar Arquivo
                    </Button>
                  </div>
                  <span className="text-xs text-gray-500">
                    {dataToVerify.length} caracteres
                  </span>
                </div>
              </div>
            </div>

            {/* Assinatura */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assinatura Digital
              </label>
              <textarea
                value={signatureToVerify}
                onChange={(e) => setSignatureToVerify(e.target.value)}
                placeholder="Cole aqui a assinatura digital (formato JSON)..."
                className="w-full p-3 border border-gray-300 rounded-md font-mono text-xs"
                rows={4}
              />
            </div>

            {/* Botão de Verificar */}
            <Button
              onClick={handleVerifySignature}
              disabled={isVerifying || !signatureToVerify.trim() || !dataToVerify.trim()}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isVerifying ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {isVerifying ? 'Verificando...' : 'Verificar Assinatura'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Assinaturas */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-600" />
              Histórico de Assinaturas
            </CardTitle>
            <CardDescription>
              Documentos assinados nesta sessão
            </CardDescription>
          </CardHeader>
          <CardContent>
            {signatureHistory.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhuma assinatura realizada ainda</p>
                <p className="text-sm text-gray-400 mt-1">
                  Assine documentos para vê-los aparecer aqui
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {signatureHistory.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{item.fileName}</h4>
                      <Badge className="bg-green-100 text-green-800">
                        Assinado
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                      <div>
                        <span className="font-medium">Data:</span>
                        <p>{new Date(item.timestamp).toLocaleString('pt-BR')}</p>
                      </div>
                      <div>
                        <span className="font-medium">Tamanho:</span>
                        <p>{formatDataSize(item.size)}</p>
                      </div>
                      <div>
                        <span className="font-medium">Hash:</span>
                        <p className="font-mono text-xs">{item.hash.substring(0, 16)}...</p>
                      </div>
                      <div>
                        <span className="font-medium">Algoritmo:</span>
                        <p>Dilithium</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => copyToClipboard(item.signature, 'Assinatura')}
                        variant="outline"
                        size="sm"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copiar Assinatura
                      </Button>
                      <Button
                        onClick={() => downloadResult(item.signature, `${item.fileName}-signature.json`)}
                        variant="outline"
                        size="sm"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Baixar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Resultados */}
      {showResults && (signatureResult || verificationResult) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {signatureResult ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Assinatura Concluída
                  </>
                ) : verificationResult?.isValid ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Assinatura Válida
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    Assinatura Inválida
                  </>
                )}
              </div>
              <Button
                onClick={() => setShowResults(false)}
                variant="ghost"
                size="sm"
              >
                ✕
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {signatureResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Algoritmo:</span>
                    <p className="font-mono">{signatureResult.algorithm}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Timestamp:</span>
                    <p>{new Date(signatureResult.timestamp).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Hash:</span>
                    <p className="font-mono text-xs">{signatureResult.hash.substring(0, 16)}...</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Status:</span>
                    <Badge className="bg-green-100 text-green-800">Sucesso</Badge>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assinatura Digital
                  </label>
                  <textarea
                    value={signatureResult.signature}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-md font-mono text-xs bg-gray-50"
                    rows={6}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(signatureResult.signature, 'Assinatura digital')}
                    variant="outline"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar
                  </Button>
                  <Button
                    onClick={() => downloadResult(
                      signatureResult.signature, 
                      `signature-${new Date().toISOString().split('T')[0]}.json`
                    )}
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Baixar
                  </Button>
                </div>
              </div>
            )}

            {verificationResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Timestamp:</span>
                    <p>{new Date(verificationResult.timestamp).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Algoritmo:</span>
                    <p className="font-mono">{verificationResult.algorithm}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Status:</span>
                    <Badge className={verificationResult.isValid ? 
                      'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }>
                      {verificationResult.isValid ? 'Válida' : 'Inválida'}
                    </Badge>
                  </div>
                </div>
                
                <div className={`p-4 rounded-lg border ${
                  verificationResult.isValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {verificationResult.isValid ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className={`font-medium ${
                      verificationResult.isValid ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {verificationResult.isValid ? 'Verificação Bem-sucedida' : 'Verificação Falhou'}
                    </span>
                  </div>
                  <p className={`text-sm ${
                    verificationResult.isValid ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {verificationResult.details}
                  </p>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Documento Verificado
                    </label>
                    <Button
                      onClick={() => setShowSensitiveData(!showSensitiveData)}
                      variant="ghost"
                      size="sm"
                    >
                      {showSensitiveData ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <textarea
                    value={maskSensitiveData(verificationResult.data, showSensitiveData)}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-md bg-gray-50"
                    rows={4}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Informações sobre Dilithium */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Sobre CRYSTALS-Dilithium
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              CRYSTALS-Dilithium é um esquema de assinatura digital pós-quântica baseado em 
              problemas de reticulado, padronizado pelo NIST para resistir a ataques de computadores quânticos.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-1">Segurança</h4>
                <p className="text-xs text-blue-700">
                  Resistente a ataques clássicos e quânticos
                </p>
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-900 mb-1">Eficiência</h4>
                <p className="text-xs text-green-700">
                  Assinaturas compactas e verificação rápida
                </p>
              </div>
              
              <div className="p-3 bg-purple-50 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-1">Padrão</h4>
                <p className="text-xs text-purple-700">
                  Aprovado pelo NIST em 2022
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PQCSignatureManager;